import { useState } from 'react';
import { SquarePlus } from 'lucide-react';
import { useSWRConfig } from 'swr';
import { NewAddressBookEntryDialog } from '@/components/address-book/new-address-book-entry-dialog';

type ImportedAddressBookNoticeProps = {
  address: string;
  label: string;
  id?: string;
};

/** Optional address-book labeling without changing an imported transaction. */
export function ImportedAddressBookNotice({
  address,
  label,
  id,
}: ImportedAddressBookNoticeProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const { mutate } = useSWRConfig();

  const handleCreated = () => {
    // Refresh exact and batched labels together: an address can appear in
    // several nested inputs. Labeling it must not re-encode the transaction.
    void mutate(
      (key) =>
        (typeof key === 'string' &&
          key.startsWith('organization/address-book/')) ||
        (Array.isArray(key) && key[0] === 'organization/address-book'),
      undefined,
      { revalidate: true, throwOnError: false }
    );
  };

  return (
    <>
      <p id={id} className="px-1 text-xs text-orange-500 dark:text-orange-300">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Add ${label} to address book`}
          aria-haspopup="dialog"
          aria-expanded={open}
          className="inline-flex items-center gap-1 rounded-sm transition-colors hover:text-orange-600 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-orange-500 dark:hover:text-orange-200"
        >
          Add to address book
          <SquarePlus aria-hidden="true" className="size-3.5 shrink-0" />
        </button>
      </p>
      <NewAddressBookEntryDialog
        open={open}
        onOpenChange={setOpen}
        onCreated={handleCreated}
        addressToLabel={address}
      />
    </>
  );
}
